import BrowserStorage from '@/services/BrowserStorage';

// all local storage items must be stored under this namespace, or they will be deleted
const NAMESPACE = 'bnef';
// known third party prefixes that we want to allow storage so we will ignore them
const THIRD_PARTY_NAMESPACES = ['mapbox', 'youbora'];

const MAX_UNUSED_TTL = 1000 * 60 * 60 * 24 * 100;
const REFRESH_TTL = 1000 * 60 * 60 * 24 * 6;

type StoredPayload<T = any> = {
    /** Timestamp of when this item was last accessed */
    accessedAt: number;
    /** Optional expiry timestamp for this item */
    expireAt?: number;
    /** The actual stored data */
    data: T;
};

/*
    A Utility for saving preferences on the client browser,
    in a way that prevents persistent storage bloat by cleaning up old/expired items.
    If this code is called on the server it will be a no-op.
 */
export class ClientStorage extends BrowserStorage {
    private static instance: ClientStorage;

    public static getInstance(): ClientStorage {
        if (!ClientStorage.instance) {
            ClientStorage.instance = new ClientStorage(
                typeof window !== 'undefined' ? window.localStorage : undefined,
                NAMESPACE
            );
        }
        return ClientStorage.instance;
    }

    public set(key: string | string[], data: any, ttlSeconds?: number): boolean {
        // before setting something new, lets cleanup any mess
        this.cleanup();

        let expire;
        if (ttlSeconds) {
            expire = Date.now() + ttlSeconds * 1000;
        }
        const payload: StoredPayload = {
            data,
            accessedAt: Date.now(),
            expireAt: expire,
        };
        return this.save(key, payload);
    }

    public get<T = any>(key: string | string[]): T | undefined {
        const item = this.retrieve<StoredPayload<T>>(key);
        if (!item) {
            return undefined;
        }

        if (item.expireAt && item.expireAt < Date.now()) {
            this.remove(key);
            return undefined;
        }

        // update the accessed time on read, if it is the first time accessing in a while
        if (item.accessedAt && item.accessedAt < Date.now() - REFRESH_TTL) {
            this.save(key, {
                ...item,
                accessedAt: Date.now(),
            });
        }

        return item.data;
    }

    /** The expiry timestamp (ms since epoch) of an item set with a TTL, or undefined when it has none or is gone */
    public getExpiry(key: string | string[]): number | undefined {
        const item = this.retrieve<StoredPayload>(key);
        if (!item?.expireAt || item.expireAt < Date.now()) {
            return undefined;
        }
        return item.expireAt;
    }

    /** Remove all stored items that have gone stale, expired, or are not part of any known namespaces */
    private cleanup(): void {
        for (const storageKey of this.getKeys()) {
            // skip known third party prefixes
            if (THIRD_PARTY_NAMESPACES.some((prefix) => storageKey.startsWith(prefix))) {
                continue;
            }
            // remove anything not in our namespace
            const keyParts = storageKey.split('.');
            if (keyParts.length < 2 || keyParts[0] !== NAMESPACE) {
                this.storage.removeItem(storageKey);
                continue;
            }

            const item = this.retrieve(storageKey, true);

            // cleanup items that explicitly have an expiry time
            if (item?.expireAt && item.expireAt < Date.now()) {
                this.storage.removeItem(storageKey);
            }

            // remove anything that was accessed a long time ago
            if (item?.accessedAt && item.accessedAt < Date.now() - MAX_UNUSED_TTL) {
                this.storage.removeItem(storageKey);
            }
        }
    }
}

export default ClientStorage.getInstance();
